import { postMessage } from '@epilot/journey-logic-commons'
import { appEventsSchema } from '@epilot/journey-logic-commons/src/schemas/v4/generated/events.zod'
import type { AppEvents } from '@epilot/journey-logic-commons/src/types/next'
import {
  APP_EVENT,
  KNOWN_EVENT_SENDERS
} from '@epilot/journey-logic-commons/src/types/next'
import { useEffect } from 'react'

import { previewHydrate } from '../store/actions/previewHydrate'
import { previewStart } from '../store/actions/previewStart'
import { previewThemeHydrate } from '../store/actions/previewThemeHydrate'
import { PORTAL_APP_URL, IS_LOCAL } from '../utils/config'
import { debug } from '../utils/debug'

/**
 * Broadcast a message telling the interested parties that preview is ready to receive data.
 * The Builder app should wait for this messsage before sending preview data to us.
 */
const ping = () => {
  previewStart(undefined)

  postMessage<AppEvents>(
    {
      type: APP_EVENT.PREVIEW_LISTENER_READY,
      from: KNOWN_EVENT_SENDERS.JOURNEY_APP
    },
    window.parent,
    { targetOrigin: '*' }
  )

  debug('[usePreview] ping sent')
}

/**
 * Listener responsible for parsing through the events and running side effects.
 * Events sent by unkown origins or invalid/malformed are silently ignored.
 */
const pong = (uncastEvent: MessageEvent<unknown>) => {
  // skip non-trustworthy origins unless running locally
  if (uncastEvent.origin !== PORTAL_APP_URL && !IS_LOCAL) return

  debug('[usePreview] pong received', uncastEvent.data)

  const parsed = appEventsSchema.safeParse(uncastEvent.data)

  if (!parsed.success) return // invalid or not our concern

  const castedMessage = parsed.data

  switch (castedMessage.from) {
    case KNOWN_EVENT_SENDERS.BUILDER_APP:
    case KNOWN_EVENT_SENDERS.DESIGN_BUILDER_APP:
      if (castedMessage.type === APP_EVENT.PREVIEW_UPDATE) {
        // @ts-expect-error until the new journey types are used and enforced
        return previewHydrate(castedMessage.payload)
      }

      if (castedMessage.type === APP_EVENT.PREVIEW_THEME_UPDATE) {
        // @ts-expect-error until the new journey types are used and enforced
        return previewThemeHydrate(castedMessage.payload)
      }
      break

    default:
      // Optional: log unhandled sender for debugging
      debug('[usePreview] Unhandled event sender', castedMessage.from)
      break
  }
}

/**
 * Handles the data exchange necessary for previewing a Journey.
 * If preview mode is disabled, the event listener is removed.
 *
 * This works in a ping-pong fashion:
 *
 * 1. We assume the Builder is ready to receive events from us, since they're
 * supposed to be embedding our App;
 * 2. (Ping) We send a "readiness" indicator event after setting up our event listener;
 * 3. (Pong) They start sending the preview data after receiving our readiness event;
 * 4. We receive the preview data and perform the side effects.
 */
export const usePreview = (enabled: boolean) => {
  useEffect(() => {
    const unlisten = () => {
      window.removeEventListener('message', pong)
      debug('[usePreview] removed event listener')
    }

    if (enabled) {
      debug('[usePreview] preview mode detected')
      window.addEventListener('message', pong, false)
      debug('[usePreview] added event listener')
      ping()
    } else {
      unlisten()
    }

    return unlisten
  }, [enabled])
}
